import { assetApi } from '../../services/assetApi';
import {
  galleryBranding,
  type GalleryBranding
} from '../../../public-gallery/galleryBranding';

const PHOTOSETS = '/asset-service/api/v1/assets/photosets';

/** asset-service's `GalleryBrandingResponse` (DV-3807). The same three
 *  fields as the public gallery's, but its own contract: declared here, so a
 *  change to either side fails to compile rather than reading a missing field. */
interface GalleryBrandingResponse {
  accountName: string | null;
  accountLogoUrl: string | null;
  accountWebsite: string | null;
}

/** Whether each email share can be sent in this environment. Both false
 *  when asset-service cannot tell. */
export interface ShareAvailability {
  team: boolean;
  gallery: boolean;
}

/** One teammate a photoset may be emailed to, as asset-service lists them:
 *  already filtered to people who can sign in and open the page today. */
export interface ShareRecipient {
  userId: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
}

/** One email send. `recipients` are user ids for the team share and email
 *  addresses for the gallery share. */
export interface PhotosetShareRequest {
  photosetId: string;
  recipients: string[];
  note?: string;
}

/** Why one recipient's email did not certainly go out (handoff §7).
 *  `REFUSED`, `RATE_LIMITED` and the switched-off codes certainly did not;
 *  `UNCONFIRMED` may have. Open-ended: a code this UI does not know is
 *  treated as not sent. */
export interface NotSent {
  recipient: string;
  outcome: string;
}

/** What became of one send, by recipient as the request named them. */
export interface PhotosetShareResult {
  sent: string[];
  notSent: NotSent[];
}

/**
 * The Share control's endpoints (DV-2102), injected into assetApi so they
 * share its cache, its reset on an account switch and its store wiring.
 */
export const shareApi = assetApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Whose branding the photoset's public gallery shows, for the Photoset
     * Gallery tab's "presented by" line. asset-service answers with the
     * gallery's own values (DV-3807: the same rule as `GET /public/gallery`,
     * so the root account, DV-3713), and the gallery header's own rule
     * (`galleryBranding`) decides logo or name, so the line names exactly
     * what a visitor sees. Null when the gallery presents no one, and for an
     * empty 200 (how a gateway can answer a route it does not know yet), so
     * the line is left out rather than the read failing on a null body.
     */
    getGalleryBranding: builder.query<GalleryBranding | null, string>({
      query: (photosetId) => `${PHOTOSETS}/${photosetId}/gallery-branding`,
      transformResponse: (response: GalleryBrandingResponse | null) =>
        response ? galleryBranding(response) : null
    }),

    /** Which email sends this environment can make (DV-3715). */
    getShareAvailability: builder.query<ShareAvailability, void>({
      query: () => `${PHOTOSETS}/share/availability`
    }),

    /**
     * Who the photoset can be emailed to (DV-3715): the members of its account
     * and the accounts above it, narrowed by asset-service to what the caller
     * may see, sorted by name. Shared with Make Visible & Notify (DV-3563).
     */
    getShareRecipients: builder.query<ShareRecipient[], string>({
      query: (photosetId) => `${PHOTOSETS}/${photosetId}/share/recipients`
    }),

    /**
     * Emails the photoset to teammates from `getShareRecipients` (DV-3715).
     * All or nothing on validation: a 400 or 409 means nobody was emailed.
     * A 502 means some may have been: never retried automatically.
     */
    shareWithTeam: builder.mutation<PhotosetShareResult, PhotosetShareRequest>({
      query: ({ photosetId, recipients, note }) => ({
        url: `${PHOTOSETS}/${photosetId}/share/team`,
        method: 'POST',
        body: { recipients, note }
      })
    })
  })
});

export const {
  useGetGalleryBrandingQuery,
  useGetShareAvailabilityQuery,
  useGetShareRecipientsQuery,
  useShareWithTeamMutation
} = shareApi;
